import React, { useRef } from 'react';
import { findDOMNode } from 'react-dom';
import classnames from 'classnames';
import { dfs, dateTypes } from '../../utils';
import { useState } from "react";
import { Modal, Button, Select, Input, ButtonGroup, Tooltip, Checkbox, HotKeys } from '@platform/base';
import { getSysFieldid } from '@platform/api';
import './index.less';

const DEFAULT_INFO = { logic: 'or', conditions: [] }; //conditions:[{key, value, logic}]

const isMultiSelected = new Set(['equal', 'notEqual']);
function filterItem(rowKey, condition, { getCellValue, getCellProps, getRowData, getColumnProps }) {
    let { key, value = [], logic = DEFAULT_ITEM_LOGIC, value2display } = condition,
        emptyValue = [undefined, null, ''];

    if (!key || key === SELECT_KEY_EMPTY) { return false; }

    let { customerFilter } = getColumnProps(key);

    value = Array.isArray(value) ? value : [value];
    
    if (value.includes(EMPTY_BLANK)) { //空白选项的处理
        value = value.filter(value => value !== EMPTY_BLANK);
        value = [...value, ...emptyValue];
    }

    let lowerCaseValue = value.map(value => typeof value === 'string' ? value?.toLowerCase() : value);
    
    if (typeof customerFilter === 'function') {
        return customerFilter(getRowData(rowKey), key, value, logic);
    } else {
        let cellValue = getCellValue(rowKey, key),
            cellDisplayString = String(cellValue._display_ || cellValue.display || cellValue.value || '').toLowerCase(),
            cellValueString = typeof cellValue.value === 'boolean' ? cellValue.value : String(cellValue.value || '').toLowerCase(),
            // cellStringValue = [String(cellValue.value)],
            // cellStringDisplay = [ String(cellValue._display_ || cellValue.display) ],
            itemType = getCellProps(rowKey, key, 'itemtype');

        // 若 itemtype 为 attachment 则值的格式为“num,pk”，只需要获取 num 进行过滤
        if (itemType === "attachment") {
            if (cellDisplayString.includes(',')) {
                cellDisplayString = cellDisplayString.split(',')[0];
            }
            if (typeof cellValueString === 'string' && cellValueString.includes(',')) {
                cellValueString = cellValueString.split(',')[0];
            }
        }

        switch (logic) {
            case 'equal':
                return value?.some(value => cellDisplayString === (value2display[value] ? value2display[value].toLowerCase() : value?.toLowerCase()));
            case 'notEqual':
                return value?.every(value => cellDisplayString !== (value2display[value] ? value2display[value].toLowerCase() : value?.toLowerCase()));
            case 'greaterThan':
                if (dateTypes.has(itemType)) {
                    return value?.some(value => cellDisplayString > value);
                } else {
                    return (itemType === 'number' || itemType === 'attachment')
                        && !isNaN(cellValueString) && !isNaN(value) &&
                        value.some(value => Number(cellValueString) > Number(value));
                }
            case 'lessThan':
                if (dateTypes.has(itemType)) {
                    return value?.some(value => cellDisplayString < value);
                } else {
                    return (itemType === 'number' || itemType === 'attachment')
                        && !isNaN(cellValueString) && !isNaN(value) &&
                        value.some(value => Number(cellValueString) < Number(value));
                }
            case 'notIncluded': // and的逻辑
                return lowerCaseValue.every(value => {
                    if (itemType === "number") {
                        return !cellDisplayString.includes(value) && !cellValueString.includes(value);
                    } else if (emptyValue.includes(value)) { //空白的逻辑
                        return value !== cellDisplayString;
                    } else {
                        return !cellDisplayString.includes(value);
                    }
                });
            case 'included': //或的逻辑
                return lowerCaseValue.some(value => {
                    if (itemType === "number") {
                        return cellDisplayString.includes(value) || cellValueString.includes(value);
                    } else if (emptyValue.includes(value)) { //空白的逻辑
                        return value === cellDisplayString;
                    } else {
                        return cellDisplayString.includes(value);
                    }
                });
        }
    }
}

function filterRows(rowKeys, filterInfo, store) {
    if (!filterInfo || !filterInfo.conditions || !filterInfo.conditions.length) { return rowKeys; }

    return rowKeys.filter(rowKey => {
        switch (filterInfo.logic) {
            case 'and':
                return filterInfo.conditions.every(condition => {
                    return filterItem(rowKey, condition, store);
                });
            case 'or':
            default:
                return filterInfo.conditions.some(condition => {
                    return filterItem(rowKey, condition, store);
                });
        }
    });
}

const { Header, Body, Footer, Title } = Modal;
const SELECT_KEY_EMPTY = ' ';
const OPTION_SELECT_ALL = 'OPTION_SELECT_ALL';
const EMPTY_BLANK = 'EMPTY_BLANK';
const DEFAULT_ITEM_LOGIC = 'equal';

export default function useFilter(Table) {
    return class FilterTable extends React.Component {

        static defaultProps = { filterConfig: {} };

        state = {
            showModal: false,
            items: [],
            logic: 'or',
            showTip: false,
        };

        savedItems = [];
        lastActiveElement = null; // 最后一次聚焦的元素，用于恢复焦点

        render() {
            let { filterConfig, columns = [], locale } = this.props;
            let { items, logic, showModal, showTip } = this.state;

            const { enable, title, onReset } = filterConfig;

            let options = [];
            let filteredKeys = new Set(this.savedItems.map(item => item.key));

            if (enable) {
                columns = dfs(columns, col => {
                    if (!col.children && col.filterable) {
                        options.push({ key: col.key, label: col.label, itemtype: col.itemtype });
                        return {
                            ...col,
                            title: (
                                <>
                                    {col.title}
                                    <Tooltip
                                        placement="top"
                                        overlay={locale['table_cancel_filter']}
                                        delayShow={300}
                                    >
                                        <i className={classnames("header-icon", { 'fixed-icon': filteredKeys.has(col.key) })}>
                                            <i
                                                className={
                                                    filteredKeys.has(col.key) ? "iconfont icon-lieguolv" : "iconfont icon-sxuan"
                                                }
                                                onClick={() => this.handleFilterIconClick(col, true)}
                                            />
                                        </i>
                                    </Tooltip>
                                </>
                            ),
                        };
                    }
                    return col;
                });
            }

            return <React.Fragment>
                <Table
                    {...this.props}
                    columns={columns}
                    filterConfig={{
                        ...filterConfig,
                        onFilterClick: this.handleFilterIconClick,
                    }}
                />
                <Modal
                    className="filter-modal"
                    visible={showModal}
                    onCancel={() => {
                        this.recoverItems();
                        this.closeModal();
                    }}
                    autoFocus={false}
                    modalAutoFocus={false} // base-modal里的属性
                    ref={dom => {
                        this.modalDOM = findDOMNode(dom);
                    }}
                    fieldid="filterPanel"
                    zIndex={1000}
                >
                    {<HotKeys
                        keyMap={{
                            handleSave: 'alt+Y',
                            handleClose: 'alt+N',
                        }}
                        handlers={{
                            handleClose: () => {
                                this.recoverItems();
                                this.closeModal();
                            },
                            handleSave: this.handleFilterClick,
                        }}
                        className="colSetting-hotkeys-wrapper"
                        focused={true}
                        attach={document.body}
                        display="inline"
                    />}
                    <Header closeButton clsPrefix='wui-modal-header'>
                        <Title
                            fieldid={getSysFieldid(`${locale['table0028' /*筛选*/]}${title || ''}`)}
                        >
                            <span className='table-filter-title'>{`${locale['table0028' /*筛选*/]}${title || ''}`}</span>
                            <span
                                title={locale['table0027' /*没有找到，换个词试一试*/]}
                                className={classnames(showTip ? 'table-filter-tips' : 'filter-tips-hidden')}
                            >
                                <span className="iconfont icon icon-gantanhao" />
                                {showTip ? locale['table0027' /*没有找到，换个词试一试*/] : ''}
                            </span>
                        </Title>
                    </Header>
                    <Body clsPrefix='wui-modal-body'>
                        <div className="condition-logic-container">
                            {locale['filter-under-condition'] /*以下条件*/}
                            <Select
                                fieldid={'filter_conditions_logic'}
                                value={logic}
                                onChange={value => {
                                    this.setState({ logic: value });
                                    if (this.lastActiveElement) {
                                        this.lastActiveElement.focus();
                                    }
                                }}
                                onBlur={() => {
                                    // change或者直接关闭都需要清空
                                    this.lastActiveElement = null;
                                }}
                                onMouseDown={() => {
                                    if (!this.lastActiveElement) {
                                        this.lastActiveElement = document.activeElement;
                                    }
                                }}
                                showClear={false}
                            >
                                <Select.Option key='and'>{locale['filter-all-condition'] /*全部*/}</Select.Option>
                                <Select.Option key='or'>{locale['filter-any-condition'] /*任一*/}</Select.Option>
                            </Select>
                            {locale['filter-accord'] /*符合*/}
                        </div>
                        {
                            items.map((item, index) => {
                                let valueOptions = this.getOptions(item.key, item.itemtype);
                                return <FilterItem
                                    key={index}
                                    item={item}
                                    valueOptions={valueOptions}
                                    keyOptions={options}
                                    onDelete={() => this.deleteItemByIndex(index)}
                                    disabled={item.key === SELECT_KEY_EMPTY}
                                    onChange={item => {
                                        items[index] = {
                                            // 先保留原 item 的字段（如 itemtype），避免 handleLogicChange/handleInputChange
                                            // 未传 itemtype 时被覆盖丢失，导致 attachment 类型下拉项不再做 num 截断
                                            ...items[index],
                                            ...item,
                                            value2display: valueOptions.reduce((obj, { display, value }) => {
                                                obj[value] = display;
                                                return obj;
                                            }, {}),
                                        };
                                        this.setState({ items });
                                    }}
                                    tagValue={item.value} //select的标签显示
                                    locale={locale}
                                    onKeyDown={e => {
                                        e.which === 13 && this.handleFilterClick();
                                    }}
                                />;
                            })
                        }
                        <div className="filter-item">
                            <div
                                className="add"
                                onClick={() => {
                                    this.addItem({ key: SELECT_KEY_EMPTY, value: [], logic: DEFAULT_ITEM_LOGIC });
                                    requestAnimationFrame(() => {
                                        this.modalDOM.querySelector('.wui-modal-body').scrollTop = 999999;
                                    });
                                }}
                                fieldid={getSysFieldid('add_btn')}
                            >
                                {`+${locale['table0029']}` /*添加条件*/}
                            </div>
                            <Button
                                // style={{ flex: 1 }}
                                onClick={() => this.setState({
                                    items: [
                                        {
                                            key: SELECT_KEY_EMPTY,
                                            value: [],
                                            logic: DEFAULT_ITEM_LOGIC,
                                        },
                                    ],
                                }, () => {
                                    this.modalDOM.querySelector('.modal-item input.wui-select-selection-search-input')?.focus();
                                })}
                                fieldid={getSysFieldid("clear")}
                            >
                                {locale['table0030'] /*清空*/}
                            </Button>

                        </div>
                    </Body>
                    <Footer clsPrefix='wui-modal-footer'>
                        <ButtonGroup>
                            <Button
                                colors="primary"
                                onClick={this.handleFilterClick}
                                className="button-primary"
                                fieldid={getSysFieldid("filter")}
                            >
                                {locale['table0028'] /*筛选*/}
                            </Button>
                            <Button
                                onClick={() => {
                                    this.setState({ items: [], logic: 'or' });
                                    this.savedItems = [];
                                    onReset?.();
                                    this.closeModal();
                                }}
                                fieldid={getSysFieldid("reset")}
                            >
                                {locale['table0031'] /*重置*/}
                            </Button>
                        </ButtonGroup>
                        <Button
                            onClick={() => {
                                this.recoverItems();
                                this.closeModal();
                            }}
                            fieldid={getSysFieldid("cancel")}
                            className="filter-cancel"
                        >
                            {locale['table_cancel'] /*取消*/}
                        </Button>
                    </Footer>
                </Modal>
            </React.Fragment>;
        }

        componentDidUpdate(prevProps) {
            let prevCondition = prevProps?.filterConfig?.conditions ?? [],
                condition = this.props?.filterConfig?.conditions || [];

            if (JSON.stringify(prevCondition) !== JSON.stringify(condition)) {
                this.savedItems = condition;
                this.setState({ items: [...condition], showTip: false });
            }
        }

        componentDidMount() {
            document.addEventListener('keydown', this.handleKeyDown);
        }

        componentWillUnmount() {
            document.removeEventListener('keydown', this.handleKeyDown);
        }

        handleKeyDown = e => {
            if (!e.ctrlKey || e.keyCode != 70) { return; } // ctrl + F

            // 焦点不在表格内
            // YBP-SCM-BUG-5329
            if (findDOMNode(this)?.contains(document.activeElement) === false) {
                return;
            }

            this.addItem({ key: SELECT_KEY_EMPTY, value: [], logic: DEFAULT_ITEM_LOGIC });
            this.setState({ showModal: true, showTip: false });
            e.preventDefault();
        }
        //点击筛选图标，打开筛选弹窗
        handleFilterIconClick = (col, clickIsDel) => {
            let filteredKeys = new Set(this.savedItems.map(item => item.key));
            if (filteredKeys.has(col.key)) {
                if (clickIsDel) { //标题上点击删除筛选条件
                    this.deleteItemByKey(col.key);
                } else { //下拉点击筛选，直接打开弹窗
                    this.setState({ showModal: true, showTip: false });
                }
            } else {
                this.addItem({ key: col.key, value: [], logic: DEFAULT_ITEM_LOGIC, itemtype: col.itemtype });
                this.setState({ showModal: true, showTip: false });
            }
        }
        //点击弹窗中的筛选按钮，筛选结果
        handleFilterClick = () => {
            let { items, logic } = this.state,
                { onFilter } = this.props.filterConfig || {};

            items = items.filter(item => item.key !== SELECT_KEY_EMPTY);

            if (!items.length) {
                items = [{ key: SELECT_KEY_EMPTY, value: [], logic: DEFAULT_ITEM_LOGIC }];
            }
            onFilter(items, logic).then(rows => {
                if (rows.length) {
                    // 保存之前的条件
                    this.saveItems(items);
                    this.closeModal();
                }
                this.setState({ items, showTip: !rows.length });
            });
        }

        closeModal = () => {
            this.setState({ showModal: false });
        }

        addItem = item => {
            const { items } = this.state;
            items.push(item);
            this.setState({ items });
        }

        deleteItemByKey = key => {
            let { items, logic } = this.state;
            const { onFilter } = this.props.filterConfig || {};

            items = items.filter(item => item.key !== key);
            onFilter?.(items, logic);

            this.saveItems(items);

            this.setState({ items });
        }

        getOptions = (key, itemtype) => {
            let { store, locale } = this.props;
            let options = [];
            let addedValue = new Set();
            if (key === SELECT_KEY_EMPTY) { //没有选择筛选列
                return [];
            }
            let visibleRows = store.getRows().filter(e => e.status != '3');
            visibleRows.forEach(row => {
                let display = row.values[key]?._display_ ?? row.values[key]?.display ?? '',
                    value = row.values[key]?.value,
                    isEmpty = String(value)?.trim() === "" || value === undefined || value === null;

                // 若 itemtype 为 attachment 则值的格式为“num,pk”，下拉选项中只显示 num
                if (itemtype === 'attachment') {
                    if (typeof display === 'string' && display.includes(',')) {
                        display = display.split(',')[0];
                    }
                    if (typeof value === 'string' && value.includes(',')) {
                        value = value.split(',')[0];
                    }
                }

                if (isEmpty) {
                    value = EMPTY_BLANK;
                }

                if (!addedValue.has(display)) { //未添加过的
                    if (isEmpty) { //空白加到第一个
                        options.unshift({
                            display: locale['table_filter_blank']/*空白*/,
                            value: EMPTY_BLANK,
                        });
                    } else {
                        options.push({
                            display: display,
                            value: value,
                        });
                    }

                    addedValue.add(display);
                }
            });

            return options;
        }

        deleteItemByIndex = index => {
            let { items } = this.state;
            items.splice(index, 1);
            this.setState({ items });
        }

        saveItems = items => {
            // 保存之前的条件
            this.savedItems = JSON.parse(JSON.stringify(items));
        }

        recoverItems = () => {
            this.setState({ items: JSON.parse(JSON.stringify(this.savedItems)) });
        }
    };
}

//弹窗Modal
function FilterItem(props) {
    const { keyOptions, valueOptions, item = {}, onChange, onDelete, disabled = false, locale, onKeyDown, tagValue } = props;
    
    const inputComponent = useRef(null);
    const filterModal = useRef(null);
    const [keyword, setKeyword] = useState('');
    const [isFocus, setIsFocus] = useState(false);

    let lastActiveElement;
    let filterValueOptions = getFilterValueOptions(keyword);

    return (
        <div className="modal-item" ref={filterModal}>
            <Select 
                value={item.key} 
                onChange={handleKeyChange} 
                showClear={false} 
                autoFocus={disabled}
                onBlur={focusLastActiveElement}
                onMouseDown={recordLastActiveElement}
                getPopupContainer={() => filterModal.current}
                onKeyDown={onKeyDown}
                fieldid={'filter_condition_attrcode'}
            >
                {
                    keyOptions.map(option => (<Select.Option key={option.key}>{option.label}</Select.Option>))
                }
            </Select>
            <Select value={item.logic} onChange={handleLogicChange} showClear={false}
                onBlur={focusLastActiveElement}
                onMouseDown={recordLastActiveElement}
                onKeyDown={onKeyDown}
                fieldid={'filter_condition_logic'}
            >
                <Select.Option key='equal'>{locale['filter-equal'] /*等于*/}</Select.Option>
                <Select.Option key='notEqual'>{locale['filter-not-equal'] /*不等于*/}</Select.Option>
                <Select.Option key='greaterThan'>{locale['filter-greater-than'] /*大于*/}</Select.Option>
                <Select.Option key='lessThan'>{locale['filter-less-than'] /*小于*/}</Select.Option>
                <Select.Option key='included'>{locale['filter-included'] /*包含*/}</Select.Option>
                <Select.Option key='notIncluded'>{locale['filter-not-included'] /*不包含*/}</Select.Option>
            </Select>
            {
                isMultiSelected.has(item.logic) ?
                    <Select
                        className="filter-item-select"
                        dropdownClassName="filter-item-option"
                        ref={inputComponent}
                        autoFocus={!disabled && !tagValue.length}
                        value={isFocus ? [] : tagValue}
                        onSelect={value => handleSelect(true, value)}
                        onDeselect={value => handleSelect(false, value)}
                        onSearch={keyword => handleSearch(keyword)}
                        onFocus={() => handleSelectFocus()}
                        onBlur={() => handleSelectBlur()}
                        filterOption={() => {
                            return true;
                        }}
                        supportSearch={true}
                        showSearch={true}
                        showClear={true}
                        autoClearSearchValue={false}
                        mode={'multiple'}
                        multiple
                        disabled={disabled}
                        onKeyDown={e => {
                            e.which === 13 &&
                                findDOMNode(inputComponent.current).querySelector('input').blur();
                            onKeyDown(e);
                        }}
                        optionLabelProp="display"
                        placeholder={locale['filter-enter']}
                        fieldid={'filter_condition_value'}
                        // open = {true}
                        notFoundContent={
                            <div className='select-empty'>
                                <i className='select-empty-icon'></i>
                                <span className='select-empty-text'>{locale['table0033'] /*暂无数据*/}</span>
                            </div>
                        }
                        virtual
                    >
                        {filterValueOptions.length > 0 &&
                            <div
                                onClickCapture={e => handleAllSelect(e, !isSelectAll(item.value), keyword)}
                                className='all-select-label'
                            >

                                <Checkbox
                                    indeterminate={isIndeterminate(item.value, valueOptions)}
                                    checked={isSelectAll(item.value)}
                                >
                                </Checkbox>
                                <span style={{ paddingLeft: '24px' }}>
                                    {locale['table_filter_all_select']/**全选*/}
                                </span>
                            </div>
                        }
                        {filterValueOptions.map(({ display, value }, index) => {
                            return <Select.Option
                                key={index}
                                value={value}
                                display={display}
                            >
                                <Checkbox
                                    className="option-checkbox"
                                    onChange={checked => handleSelect(checked, value)}
                                    checked={!!item.value?.includes(value)}
                                >
                                </Checkbox>
                                <span
                                    style={{ paddingLeft: '24px' }}
                                >
                                    {highLight(display)}
                                </span>
                            </Select.Option>;
                        })}
                    </Select>
                    :
                    <Input
                        ref={inputComponent}
                        value={item.value}
                        onChange={handleInputChange}
                        autoFocus={!disabled}
                        disabled={disabled}
                        onKeyDown={onKeyDown}
                        fieldid={'filter_condition_value'}
                    />
            }
            <i className="iconfont icon-shanchu" onClick={onDelete} />
        </div>

    );

    // 切换字段
    function handleKeyChange(key) {
        onChange?.({ key, value: [], logic: item.logic, itemtype: keyOptions.find(e => e.key === key).itemtype });
        setTimeout(() => { // 延迟，确保输入框已解除disabled
            findDOMNode(inputComponent.current).querySelector('input').focus();
        });
    }

    //点击checkbox时触发
    function handleSelect(checked, value) {

        let currentValue = [];

        if (value === '' || value === null || value === undefined) { //清空按钮
            onChange?.({ key: item.key, value: [], logic: item.logic, itemtype: item.itemtype });
            return;
        }
        if (checked) {
            currentValue = [...new Set([...item.value, value])];
        } else {
            currentValue = item.value.filter(option => {
                if (option !== value) {
                    return value;
                }
            });

        }
        onChange?.({ key: item.key, value: currentValue, logic: item.logic, itemtype: item.itemtype });
    }

    // 全选逻辑
    function handleAllSelect(e, checked, keyword) {
        let selectValue = [];
        
        if (checked) {
            selectValue = getFilterValueOptions(keyword).map(e => e.value);
        }

        onChange?.({ key: item.key, value: selectValue, logic: item.logic, itemtype: item.itemtype });
        e?.stopPropagation();
    }

    // 搜索逻辑
    function handleSearch(keyword) {
        setKeyword(keyword);
        onChange?.({ key: item.key, value: getFilterValueOptions(keyword).map(e => e.value), logic: item.logic, itemtype: item.itemtype });
    }

    function handleSelectFocus() {
        setIsFocus(true);
    }

    // select-blur
    function handleSelectBlur() {
        if (keyword) {
            if (!item.value?.length) {
                item.value.push(...keyword.split(/,|，|\s/)); //展示在tag上用于搜索
            }
            onChange?.({ key: item.key, value: item.value, logic: item.logic, itemtype: item.itemtype });
        }
        setKeyword('');
        setIsFocus(false);
    }
    //判断是否全选 (selectValue ≥ option的值)
    function isSelectAll(selectValue = []) {
        let optionValue = getFilterValueOptions(keyword).map(e => e.value);
        let selectValueSet = new Set(selectValue);
        return optionValue.every(option => selectValueSet.has(option));
    }
    //判断是否半选
    function isIndeterminate(selectValue = [], optionValue = []) {
        let selectValueSet = new Set(selectValue);
        return optionValue.some(option => selectValueSet.has(option));
    }
    //获取当前面板上的选项
    function getFilterValueOptions(keyword) {
        if (keyword) {
            return valueOptions.filter(({ display, value }) => {
                if (item.itemtype === 'number') {
                    // 数值类型：去除千分位分隔符后再匹配，带/不带千分位均可搜索到
                    // value 可能为数值类型，统一转为字符串避免 indexOf 报错
                    let kw = String(keyword).replace(/[,，]/g, ''),
                        numDisplay = String(display ?? '').replace(/[,，]/g, ''),
                        numValue = String(value ?? '').replace(/[,，]/g, '');
                    return numDisplay.indexOf(kw) > -1 || numValue.indexOf(kw) > -1;
                }
                return display.indexOf(keyword) > -1;
            });
        }
        return valueOptions;
    }

    //单选 
    function handleInputChange(value) {
        onChange?.({ key: item.key, value: [value], logic: item.logic });
    }

    function handleLogicChange(value) {
        onChange?.({ key: item.key, value: item.value, logic: value });
        setTimeout(() => { // 延迟，确保输入框已解除disabled
            findDOMNode(inputComponent.current).querySelector('input').focus();
        });
    }

    function recordLastActiveElement() {
        if (!lastActiveElement) {
            lastActiveElement = document.activeElement;
        }
    }

    function focusLastActiveElement() {
        // Select blur 触发此函数
        // 未做选择关闭弹窗，需要复原焦点，这里不用考虑onChange触发，因为那边有延迟
        lastActiveElement && lastActiveElement.focus();
        lastActiveElement = null;
    }

    function highLight(text) {
        let isMatch = false;
        return text?.split('').map((str, index) => {
            if (str === keyword && !isMatch) {
                //只匹配第一个
                isMatch = true;
                return [<span key={index} style={{ color: 'red' }}>{str}</span>];
            } else {
                return str;
            }
        });
    }
}

useFilter.displayName = 'useFilter';
useFilter.dependence = [];
useFilter.hooks = function () {
    this._filter = rowKeys => {
        return filterRows(rowKeys, this.store.filterInfo, { ...this });
    };
    return {
        getFilterInfo: () => {
            return this.store.filterInfo || DEFAULT_INFO;
        },
        setFilterInfo: info => {
            this.store.filterInfo = info || DEFAULT_INFO;

            this._updateVisibleRows();
            this._updateTotal();
            this.forceUpdate();
        },
    };
};
useFilter.propsGenerator = function (props) {
    if (!props.filterConfig?.enable) {
        return props;
    }

    let { store, filterConfig } = props;
    let info = store.getFilterInfo();

    return {
        ...props,
        filterConfig: {
            ...filterConfig,
            ...info,
            onFilter: (conditions, logic) => {
                return new Promise(resolve => {
                    let rowKeys = store.getRowKeys();
                    let info = store.getFilterInfo();

                    let newInfo = { ...info, conditions: [...conditions], logic };
                    let filterRowKeys = filterRows(rowKeys, newInfo, { ...store });
                    if (filterRowKeys.length) {
                        store.setFilterInfo(newInfo);
                    }

                    filterConfig?.onFilter?.();
                    resolve(filterRowKeys);
                });
            },
            onClear: () => {
                store.setFilterInfo(DEFAULT_INFO);
                filterConfig?.onClear?.();
            },
            onReset: () => {
                store.setFilterInfo(DEFAULT_INFO);
                filterConfig?.onReset?.();
            },
        },
    };
};